import React, { useRef, useState } from "react";
import WebcamRecorder from "./WebcamRecorder";
import ScreenCaptureRecorder from "./ScreenCaptureRecorder";
import { createTimestampedFolderName, uploadToGCP, uploadTextFile, formatEmotionData } from "../../utils/recordingUtils";
import { getCurrentUserId } from "../../services/dashboardService";
import { submitEvidence, getClientIP } from "../../services/antiFraudService";
import axios from "axios";
import { getToken } from "../../services/userService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Recording Manager Component
 * Orchestrates webcam and screen capture recording, manages lifecycle, and handles uploads.
 * Review stills are extracted server-side from uploaded videos (not captured during play).
 * Evidence URLs are written independently as each GCS upload completes.
 */
const RecordingManager = React.forwardRef(({ assignmentId, gameId, username, onRecordingStarted, onRecordingStopped, onError, getScreenCaptureStream }, ref) => {
  const webcamRecorderRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimestamp, setRecordingTimestamp] = useState(null);
  const [recordingId, setRecordingId] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ webcam: false, gameplay: false, emotion: false });
  const uploadPromisesRef = useRef([]);
  const uploadedUrlsRef = useRef({ webcamVideoUrl: null, gameplayVideoUrl: null });
  const recordingTimestampRef = useRef(null);
  const recordingStartMsRef = useRef(null);
  const recordingIdRef = useRef(null);
  const [validationResult, setValidationResult] = useState(null);

  const sanitizeEmailForFolder = (emailValue) => {
    if (typeof emailValue !== "string") return "";
    return emailValue
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  // Get username from localStorage if not provided
  const getUsername = () => {
    if (typeof username === "string" && username.trim()) return username.trim();
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        if (typeof parsed?.username === "string" && parsed.username.trim()) {
          return parsed.username.trim();
        }

        const sanitizedEmail =
          sanitizeEmailForFolder(parsed?.email) || sanitizeEmailForFolder(parsed?.userEmail);
        if (sanitizedEmail) {
          return sanitizedEmail;
        }

        if (typeof parsed?.name === "string" && parsed.name.trim()) {
          return parsed.name.trim();
        }

        return `user${getCurrentUserId() || "unknown"}`;
      } catch (e) {
        return `user${getCurrentUserId() || "unknown"}`;
      }
    }
    const storedUsername = localStorage.getItem("username");
    if (typeof storedUsername === "string" && storedUsername.trim()) return storedUsername.trim();
    const storedEmail = localStorage.getItem("email") || localStorage.getItem("userEmail");
    const sanitizedStoredEmail = sanitizeEmailForFolder(storedEmail);
    if (sanitizedStoredEmail) return sanitizedStoredEmail;
    const userId = getCurrentUserId();
    return userId ? `user${userId}` : "user";
  };

  const getElapsedRecordingSeconds = () => {
    if (!recordingStartMsRef.current) return 0;
    return (Date.now() - recordingStartMsRef.current) / 1000;
  };

  const applyValidationResult = (result) => {
    if (!result?.validation) return;
    setValidationResult(result.validation);
    if (result.validation.isApproved) {
      console.log("✅ Session auto-approved!", result.validation);
    } else if (result.validation.requiresReview) {
      console.warn("⚠️ Session flagged for review:", result.validation.flags);
    } else if (result.validation.isRejected) {
      console.error("❌ Session rejected:", result.validation.flags);
    }
  };

  /**
   * Persist a single evidence URL as soon as its GCS upload finishes.
   * Gameplay and webcam are independent — one failure cannot block the other.
   */
  const persistEvidenceUrl = async (partial) => {
    const id = recordingIdRef.current;
    if (!id) {
      console.warn("Cannot persist evidence URL: recordingId not available");
      return null;
    }
    try {
      const result = await submitEvidence(id, partial);
      applyValidationResult(result);
      return result;
    } catch (err) {
      console.error("Error persisting evidence URL:", err);
      throw err;
    }
  };

  // Start recording session
  const startRecording = async () => {
    try {
      setError(null);
      const timestamp = createTimestampedFolderName();
      recordingTimestampRef.current = timestamp;
      setRecordingTimestamp(timestamp);

      // Initialize recording session on backend
      try {
        const response = await axios.post(
          `${API_BASE_URL}/recordings/start`,
          {
            assignmentId,
            gameId,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${getToken()}`,
            },
          }
        );

        if (response.data && response.data.recordingId) {
          recordingIdRef.current = response.data.recordingId;
          setRecordingId(response.data.recordingId);
        }
      } catch (err) {
        console.warn("Failed to initialize recording session on backend:", err);
        // Continue recording even if backend initialization fails
      }

      // Start webcam recording
      if (webcamRecorderRef.current) {
        await webcamRecorderRef.current.start();
      }

      // Start screen capture recording (pass existing stream if available)
      if (screenRecorderRef.current) {
        // Get the current stream from the getter function
        const stream = getScreenCaptureStream ? getScreenCaptureStream() : null;
        await screenRecorderRef.current.start(stream);
      }

      recordingStartMsRef.current = Date.now();
      setIsRecording(true);

      if (onRecordingStarted) {
        onRecordingStarted(timestamp);
      }
    } catch (err) {
      console.error("Error starting recording:", err);
      const errorMsg = err.message || "Failed to start recording";
      setError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
    }
  };

  // Stop recording and upload files
  const stopRecording = async () => {
    try {
      setError(null);
      uploadPromisesRef.current = []; // Reset upload promises
      uploadedUrlsRef.current = { webcamVideoUrl: null, gameplayVideoUrl: null };

      // Capture paid duration from the recording clock BEFORE uploads (upload time must not be billed).
      const evidenceDurationSeconds = getElapsedRecordingSeconds();
      recordingStartMsRef.current = null;

      // Stop recorders (finalizes blobs and kicks off uploads without blocking on GCS)
      const stopPromises = [];
      if (webcamRecorderRef.current) {
        stopPromises.push(webcamRecorderRef.current.stop());
      }
      if (screenRecorderRef.current) {
        stopPromises.push(screenRecorderRef.current.stop());
      }

      await Promise.all(stopPromises);

      // Mark recording complete on backend immediately using media duration
      if (recordingIdRef.current || recordingId) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/recordings/${recordingIdRef.current || recordingId}/stop`,
            { evidenceDurationSeconds },
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${getToken()}`,
              },
            }
          );

          // Display earnings information to user
          if (response.data) {
            const { duration, cashEarned, totalCashEarned } = response.data;
            if (duration !== undefined && cashEarned !== undefined) {
              const cashEarnedNum = Number(cashEarned) || 0;
              const totalCashEarnedNum =
                totalCashEarned !== undefined && totalCashEarned !== null
                  ? Number(totalCashEarned)
                  : cashEarnedNum;
              const earningsMsg = `Session complete! You earned $${cashEarnedNum.toFixed(2)} for ${duration} minutes of gameplay. Total earnings: $${totalCashEarnedNum.toFixed(2)}`;
              console.log(earningsMsg);
              // Notify parent component with earnings data
              if (onRecordingStopped) {
                onRecordingStopped({
                  duration,
                  cashEarned: cashEarnedNum,
                  totalCashEarned: totalCashEarnedNum,
                });
              }
            } else {
              // Backend responded but no earnings data - still notify that recording stopped
              if (onRecordingStopped) {
                onRecordingStopped();
              }
            }
          }
        } catch (err) {
          console.warn("Failed to mark recording as complete on backend:", err);
          // Still notify that recording stopped even if backend call failed
          if (onRecordingStopped) {
            onRecordingStopped();
          }
        }
      } else {
        // No recordingId - recording stopped but wasn't tracked on backend
        console.warn("No recordingId available to stop recording session");
        if (onRecordingStopped) {
          onRecordingStopped();
        }
      }

      // Give callbacks a moment to add upload promises to the ref
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Wait for independent GCS→DB patches; failures are isolated per asset.
      if (uploadPromisesRef.current.length > 0) {
        const results = await Promise.allSettled(uploadPromisesRef.current);
        const failed = results.filter((r) => r.status === "rejected");
        if (failed.length > 0) {
          console.warn("Some uploads failed:", failed.map((r) => r.reason));
        }
      }

      setIsRecording(false);
    } catch (err) {
      console.error("Error stopping recording:", err);
      const errorMsg = err.message || "Failed to stop recording";
      setError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
    }
  };

  // Handle webcam recording stop and upload
  const handleWebcamStop = async ({ videoBlob, emotionData }) => {
    if (!recordingTimestamp && !recordingTimestampRef.current) return;

    const uploadPromise = (async () => {
      try {
        setUploadProgress((prev) => ({ ...prev, webcam: true }));

        const folderPath = `videos/${getUsername()}/${recordingTimestampRef.current || recordingTimestamp}`;

        // Upload webcam video
        const uploadResult = await uploadToGCP(videoBlob, "webcam.webm", folderPath, "video/webm");

        uploadedUrlsRef.current.webcamVideoUrl = uploadResult.url;

        // Persist webcam URL independently (does not wait for gameplay)
        await persistEvidenceUrl({ webcamVideoUrl: uploadResult.url });

        setUploadProgress((prev) => ({ ...prev, webcam: false }));

        // Upload emotion data file
        if (emotionData && emotionData.length > 0) {
          setUploadProgress((prev) => ({ ...prev, emotion: true }));

          const formattedData = formatEmotionData(emotionData);
          await uploadTextFile(formattedData, "face_annotation.txt", folderPath);

          setUploadProgress((prev) => ({ ...prev, emotion: false }));
        }
      } catch (err) {
        console.error("Error uploading webcam recording:", err);
        setError(`Failed to upload webcam recording: ${err.message}`);
        throw err;
      }
    })();

    // Queue upload but do not await it here — paid stop must not wait on GCS.
    uploadPromisesRef.current.push(uploadPromise);
  };

  // Handle screen capture stop and upload
  const handleScreenStop = async ({ videoBlob, mimeType }) => {
    if (!recordingTimestamp && !recordingTimestampRef.current) return;

    const uploadPromise = (async () => {
      try {
        setUploadProgress((prev) => ({ ...prev, gameplay: true }));

        if (!videoBlob || videoBlob.size === 0) {
          throw new Error("Gameplay recording blob is empty. Upload skipped.");
        }

        const folderPath = `videos/${getUsername()}/${recordingTimestampRef.current || recordingTimestamp}`;
        const fileName = mimeType.includes("webm") ? "gameplay.webm" : "gameplay.mp4";

        const uploadResult = await uploadToGCP(videoBlob, fileName, folderPath, mimeType);

        uploadedUrlsRef.current.gameplayVideoUrl = uploadResult.url;

        // Persist gameplay URL + session meta independently (does not wait for webcam)
        const webcamChecksCount = webcamRecorderRef.current?.getWebcamChecksCount?.() || 0;
        const webcamVerified = webcamRecorderRef.current?.isFaceDetected?.() || false;
        let ipAddress;
        try {
          ipAddress = await getClientIP();
        } catch (_) {
          ipAddress = undefined;
        }

        await persistEvidenceUrl({
          gameplayVideoUrl: uploadResult.url,
          webcamVerified,
          webcamChecksCount,
          ipAddress,
          userAgent: navigator.userAgent,
        });

        setUploadProgress((prev) => ({ ...prev, gameplay: false }));
      } catch (err) {
        console.error("Error uploading gameplay recording:", err);
        setError(`Failed to upload gameplay recording: ${err.message}`);
        throw err;
      }
    })();

    // Queue upload but do not await it here — paid stop must not wait on GCS.
    uploadPromisesRef.current.push(uploadPromise);
  };

  // Expose start/stop methods
  React.useImperativeHandle(ref, () => ({
    start: startRecording,
    stop: stopRecording,
    isRecording: () => isRecording,
  }));

  return (
    <div className="recording-manager">
      <WebcamRecorder
        ref={webcamRecorderRef}
        onRecordingStop={handleWebcamStop}
        recordingTimestamp={recordingTimestamp}
        username={getUsername()}
        recordingId={recordingId}
      />
      <ScreenCaptureRecorder
        ref={screenRecorderRef}
        onRecordingStop={handleScreenStop}
        getExistingStream={getScreenCaptureStream}
        onStopRequested={stopRecording}
      />
      {error && <div className="recording-error">Error: {error}</div>}
      {(uploadProgress.webcam || uploadProgress.gameplay || uploadProgress.emotion) && (
        <div className="upload-progress">
          Uploading: {uploadProgress.webcam && "Webcam "}
          {uploadProgress.gameplay && "Gameplay "}
          {uploadProgress.emotion && "Emotion Data"}
        </div>
      )}
      {validationResult && (
        <div className={`validation-result validation-${validationResult.status}`}>
          {validationResult.isApproved && "✅ Session approved!"}
          {validationResult.requiresReview && "⚠️ Session flagged for review"}
          {validationResult.isRejected && "❌ Session rejected"}
        </div>
      )}
    </div>
  );
});

export default RecordingManager;
